import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Handover as Report, type HandoverDose, type OrgSummary } from '../api.ts';

/**
 * The shift handover — RX-ORG-09.
 *
 * What one nurse hands to the next: what is still owed from the shift that is
 * ending, and what falls due during the one beginning. The endpoint and the
 * sorting rules have existed for weeks with no screen to show them.
 *
 * It is a worklist, not a report. Outstanding first, oldest first, criticals
 * ahead of ordinary ones at the same age — then the coming rounds grouped by
 * time and walked in room order, so a nurse crosses the building once.
 *
 * Printable on purpose: a great many homes still carry paper to the round,
 * and a screen that cannot be printed is one that gets transcribed by hand.
 */
const HOURS = [4, 8, 12] as const;

const FOOD: Record<string, string> = {
  before_food: 'before food',
  with_food: 'with food',
  after_food: 'after food',
  empty_stomach: 'empty stomach',
};

function time(ms: number, zone: string): string {
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', timeZone: zone,
  });
}

export function Handover({ org }: { org: OrgSummary }) {
  const [hours, setHours] = useState<number>(8);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<{ handover: Report; scoped: boolean }>(
        `/api/orgs/${org.id}/handover?hours=${hours}`,
      );
      setReport(d.handover);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [org.id, hours]);

  useEffect(() => { void load(); }, [load]);

  if (loading && !report) return <p className="text-(--muted)">Loading…</p>;
  if (error) {
    return (
      <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500">
        {error}
      </p>
    );
  }
  if (!report) return null;

  const zone = report.timeZone;

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <h2 className="text-lg font-semibold">Handover</h2>
        <div className="flex gap-2">
          {HOURS.map((h) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              aria-pressed={hours === h}
              className={`min-h-11 rounded-lg border px-3 text-sm ${
                hours === h
                  ? 'border-(--accent) bg-(--accent-soft) text-(--accent)'
                  : 'border-(--line) hover:border-(--accent)'
              }`}
            >
              Next {h}h
            </button>
          ))}
        </div>
        <button
          onClick={() => window.print()}
          className="ml-auto min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
        >
          Print
        </button>
      </div>

      <div className="rounded-xl border border-(--line) bg-(--panel) p-4">
        <p className="text-sm text-(--muted)">
          {org.name} · {time(report.from, zone)}–{time(report.to, zone)} ·{' '}
          {zone.replace(/_/g, ' ')}
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Count label="Residents" value={report.counts.residents} />
          <Count label="Outstanding" value={report.counts.outstanding} urgent={report.counts.outstanding > 0} />
          <Count label="Critical" value={report.counts.critical} urgent={report.counts.critical > 0} />
          <Count label="Due" value={report.counts.due} />
          <Count label="Given" value={report.counts.given} />
        </dl>

        {/*
          Said, not hidden. A worklist that silently stopped at a row cap is
          one a nurse trusts to be the whole shift, and it is not.
        */}
        {!report.complete && (
          <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
            This is more than one screen can hold, so it is cut short. Use a
            shorter window to see all of it.
          </p>
        )}
      </div>

      {report.outstanding.length > 0 && (
        <div className="rounded-xl border border-red-500/30 p-4">
          <h3 className="font-medium text-red-600">
            Still owed from the last shift ({report.outstanding.length})
          </h3>
          <p className="mt-1 text-xs text-(--muted)">
            Oldest first, and criticals ahead of the rest at the same age.
          </p>
          <ul className="mt-3 space-y-2">
            {report.outstanding.map((d) => (
              <Dose key={d.doseEventId} dose={d} zone={zone} overdue />
            ))}
          </ul>
        </div>
      )}

      {report.slots.map((slot) => (
        <div key={slot.at} className="rounded-xl border border-(--line) p-4">
          <h3 className="font-medium">
            {slot.label}
            <span className="ml-2 text-sm font-normal text-(--muted)">
              {slot.doses.length} to give
            </span>
          </h3>
          <ul className="mt-3 space-y-2">
            {slot.doses.map((d) => (
              <Dose key={d.doseEventId} dose={d} zone={zone} />
            ))}
          </ul>
        </div>
      ))}

      {report.outstanding.length === 0 && report.slots.length === 0 && (
        <p className="rounded-xl border border-dashed border-(--line) p-8 text-center text-sm text-(--muted)">
          Nothing due in the next {hours} hours, and nothing outstanding.
        </p>
      )}

      <p className="text-xs text-(--muted)">
        DrBell records what was given. It does not decide what should be — that
        is the prescription, and a question for the prescriber.
      </p>
    </section>
  );
}

function Count({ label, value, urgent }: { label: string; value: number; urgent?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-(--muted)">{label}</dt>
      <dd className={`text-2xl font-semibold tabular-nums ${urgent ? 'text-red-600' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function Dose({ dose, zone, overdue }: { dose: HandoverDose; zone: string; overdue?: boolean }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-(--line) pb-2 last:border-0 last:pb-0">
      {/* Room leads. It is what a nurse navigates by, and the list is sorted
          into the order the corridor is actually walked. */}
      <span className="w-14 shrink-0 font-medium tabular-nums">
        {dose.locationNote ?? '—'}
      </span>
      <span className="font-medium">{dose.patientName}</span>
      <span>
        {dose.medicationName}
        {dose.strength && (
          <span className="text-(--muted)"> {dose.strength}{dose.strengthUnit ?? ''}</span>
        )}
      </span>
      <span className="text-(--muted)">
        {dose.doseQuantity} {dose.doseUnit}
      </span>
      {dose.foodRelation && dose.foodRelation !== 'any' && (
        <span className="text-(--muted)">· {FOOD[dose.foodRelation] ?? dose.foodRelation}</span>
      )}
      {dose.isCritical && (
        <span className="rounded-full border border-red-500/40 px-2 text-xs text-red-500">
          critical
        </span>
      )}
      {overdue && (
        <span className="ml-auto text-xs tabular-nums text-red-500">
          due {time(dose.dueAt, zone)}
        </span>
      )}
      {dose.status === 'taken' && (
        <span className="ml-auto text-xs text-(--accent)">given</span>
      )}
    </li>
  );
}
